import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { append, iif, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CustomerUsersService } from '../../services/customerUsers.service';
import { Right } from '../../services/dtos/right';
import { User } from '../../services/dtos/user';

import { UserEmailTemplate } from 'src/app/services/dtos/userEmailTemplate';
import {
  AddCustomerUser,
  AddUser, AddUserEmailTemplate, DeleteCustomerUser, DeleteUser, DeleteUserEmailTemplate, FetchAdminUsers, FetchAllRightsIfEmpty,
  FetchAllUsers, GetUserEmailTemplates, UpdateCustomerUser,
  UpdateUser, UpdateUserEmailTemplate
} from './customerUsers.actions';

export class CustomerUsersStateModel {
  public users!: User[];
  public adminUsers!: User[];
  public rights!: Right[];
  public selectedUserEmailTemplates!: UserEmailTemplate[];
}

const defaults = {
  users: [],
  adminUsers: [],
  rights: [],
  selectedUserEmailTemplates: []
};

@State<CustomerUsersStateModel>({
  name: 'customerUsersState',
  defaults
})

@Injectable()
export class CustomerUsersState {
  public constructor(private customerUsersService: CustomerUsersService) { }

  @Selector()
  public static getUsers(state: CustomerUsersStateModel): User[] {
    return state.users;
  }

  // eslint-disable-next-line
  public static getCustomerUsers(customerId: string) {
    return createSelector([CustomerUsersState], (state: CustomerUsersStateModel) =>
      state.users.filter(x => x.customers.includes(customerId)));
  }

  @Selector()
  public static getAdminUsers(state: CustomerUsersStateModel): User[] {
    return state.adminUsers;
  }

  @Selector()
  public static getRights(state: CustomerUsersStateModel): Right[] {
    return state.rights;
  }

  @Selector()
  public static getUserEmailTemplates(state: CustomerUsersStateModel): UserEmailTemplate[] {
    return state.selectedUserEmailTemplates;
  }

  @Action(FetchAllRightsIfEmpty)
  public fetchAllRights({ getState, setState }: StateContext<CustomerUsersStateModel>): Observable<Right[]> | void {
    if (getState().rights.length === 0) {
      return this.customerUsersService.getAllRights()
        .pipe(
          tap((rights: Right[]) => {
            const state = getState();
            setState({ ...state, rights });
          })
        );
    }
  }

  @Action(FetchAdminUsers)
  public fetchAdminUsers({ getState, setState }: StateContext<CustomerUsersStateModel>): Observable<User[]> | void {
    return this.customerUsersService.getAdminOnly()
      .pipe(
        tap((adminUsers: User[]) => {
          const state = getState();
          setState({ ...state, adminUsers });
        })
      );
  }

  @Action(FetchAllUsers)
  public fetchAll({ getState, setState }: StateContext<CustomerUsersStateModel>): Observable<User[]> {
    return this.customerUsersService.getAll()
      .pipe(
        tap((freshCustomerUsers: User[]) => {
          const state = getState();
          freshCustomerUsers.forEach(user => {
            setState(
              patch<CustomerUsersStateModel>({
                users: iif<User[]>(
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  customers => customers!.some(x => x.id === user.id),
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  updateItem<User>(x => x!.id === user.id, user),
                  insertItem<User>(user)
                )
              })
            );
          });
          // remove
          const customerUsersToRemove = state.users.filter(c => freshCustomerUsers.every(fc => fc.id !== c.id));
          customerUsersToRemove.forEach(user => {
            setState(
              patch<CustomerUsersStateModel>({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                users: removeItem<User>(x => x!.id === user.id)
              })
            );
          });
        })
      );
  }

  @Action(DeleteUser)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public delete({ setState }: StateContext<CustomerUsersStateModel>, { id }: DeleteUser): Observable<any> {
    return this.customerUsersService.deleteUser(id)
      .pipe(
        tap(() => {
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              adminUsers: removeItem<User>(x => x!.id === id),
              users: removeItem<User>(x => x?.id === id)
            })
          );
        })
      );
  }

  @Action(UpdateUser)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public update({ setState }: StateContext<CustomerUsersStateModel>, { payload }: UpdateUser): Observable<any> {
    return this.customerUsersService.updateUser(payload)
      .pipe(
        tap(() => {
          const user = {
            id: payload.id,
            eMailAddress: payload.eMailAddress,
            name: payload.name,
            hasPassword: payload.hasPassword || (payload.isPasswordUpdated && payload.password !== ''),
            laboratories: payload.laboratories,
            role: payload.role,
            roleName: payload.roleName
          } as User;
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              adminUsers: updateItem<User>(x => x!.id === user.id, user)
            })
          );
        })
      );
  }

  @Action(AddUser)
  public add({ setState }: StateContext<CustomerUsersStateModel>, { payload }: AddUser): Observable<string> {
    return this.customerUsersService.addUser(payload)
      .pipe(
        tap(userId => {
          const user = {
            id: userId,
            eMailAddress: payload.eMailAddress,
            name: payload.name,
            hasPassword: payload.password != null && payload.password !== '',
            laboratories: payload.laboratories,
            role: payload.role,
            roleName: payload.roleName
          } as User;
          setState(
            patch({
              adminUsers: append<User>([user])
            })
          );
        })
      );
  }

  @Action(UpdateCustomerUser)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public updateCustomerUser({ getState, setState }: StateContext<CustomerUsersStateModel>, { payload }: UpdateCustomerUser): Observable<any> {
    return this.customerUsersService.updateCustomerUser(payload)
      .pipe(
        tap(() => {
          const state = getState();
          const oldUser = state.users.find(x => x.id === payload.id);
          const user = {
            id: payload.id,
            eMailAddress: payload.eMailAddress,
            name: payload.name,
            hasPassword: payload.hasPassword || (payload.isPasswordUpdated && payload.password !== ''),
            laboratories: oldUser?.laboratories,
            customers: oldUser?.customers,
            role: payload.role,
            roleName: payload.roleName,
            loginName: payload.loginName
          } as User;
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              users: updateItem<User>(x => x!.id === user.id, user)
            })
          );
        })
      );
  }

  @Action(AddCustomerUser)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addCustomerUser({ getState, setState }: StateContext<CustomerUsersStateModel>, { payload, customerId }: AddCustomerUser): Observable<string> {
    return this.customerUsersService.addCustomerUser(customerId, payload)
      .pipe(
        tap(userId => {
          const user = getState().users.find(x => x.id === userId);
          let userPayload: User;
          if (user) {
            const customers = [...user.customers];
            customers.push(customerId);
            userPayload = { ...user, customers } as User;
          }
          else {
            userPayload = {
              id: userId,
              eMailAddress: payload.eMailAddress,
              name: payload.name,
              hasPassword: payload.password != null && payload.password !== '',
              laboratories: [],
              customers: [customerId],
              role: payload.role,
              roleName: payload.roleName,
              loginName: payload.loginName
            } as User;
          }
          setState(
            patch({
              users: iif<User[]>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                customers => customers!.some(x => x.id === userId),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                updateItem<User>(x => x!.id === userId, userPayload),
                insertItem<User>(userPayload)
              )
            })
          );
        })
      );
  }

  @Action(DeleteCustomerUser)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public deleteCustomerUser({ setState }: StateContext<CustomerUsersStateModel>, { customerId, userId }: DeleteCustomerUser): Observable<any> {
    return this.customerUsersService.deleteCustomerUser(customerId, userId)
      .pipe(
        tap(() => {
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              users: removeItem<User>(x => x!.id === userId)
            })
          );
        })
      );
  }

  @Action(AddUserEmailTemplate)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addUserEmailTemplate({dispatch }: StateContext<CustomerUsersStateModel>, { payload }: AddUserEmailTemplate): Observable<string> {
    return this.customerUsersService.addUserEmailTemplate(payload)
    .pipe(
      tap(() => {
        dispatch(new GetUserEmailTemplates(payload.userId, payload.customerId || ''));
      })
    );
  }

  @Action(UpdateUserEmailTemplate)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public updateUserEmailTemplate({ dispatch }: StateContext<CustomerUsersStateModel>, { payload }: UpdateUserEmailTemplate): Observable<any> {
    return this.customerUsersService.updateUserEmailTemplate(payload)
    .pipe(
      tap(() => {
        dispatch(new GetUserEmailTemplates(payload.userId, payload.customerId || ''));
      })
    );
  }

  @Action(DeleteUserEmailTemplate)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public deleteUserEmailTemplate({ dispatch }: StateContext<CustomerUsersStateModel>, { emailTemplateId, customerId, userId }: DeleteUserEmailTemplate): Observable<any> {
    return this.customerUsersService.deleteUserEmailTemplate(emailTemplateId, userId, customerId || '')
    .pipe(
      tap(() => {
        dispatch(new GetUserEmailTemplates(userId, customerId || ''));
      })
    );
  }

  @Action(GetUserEmailTemplates)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public getUserEmailTemplates({ getState, setState }: StateContext<CustomerUsersStateModel>, { userId, customerId }: GetUserEmailTemplates): Observable<any> {
    return this.customerUsersService.getUserEmailTemplates(userId, customerId || '')
    .pipe(
      tap((userEmailTemplates: UserEmailTemplate[]) => {
        const state = getState();
        setState({
          ...state,
          selectedUserEmailTemplates: userEmailTemplates
        });
      })
    );
  }
}
